import { useCommissionAccess } from "@/hooks/useCommissionAccess";
import {
  Menu,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { useLocation } from "wouter";
import { Breadcrumbs } from "./components/breadcrumbs";
import { Routes } from "./components/routes";
import { TooltipProvider } from "@radix-ui/react-tooltip";
import { UserNav } from "./components/user-nav";
import { CalendarDateRangePicker } from "./components/date-range-picker";
import { useAppStore } from "@/stores";
import { ListItem, TODO } from "@revelate/types";
import { useScope } from "@/hooks/useScope";
import useSWR from "swr";
import { TableType, get } from "@/lib/supabase";
import { useEffect, useState } from "react";
import { getList, getScope } from "@/lib/app";
import { Loader } from "@/components/Loader";
import { parseISO } from "date-fns";
import { AppSidebar, DesktopSidebarContainer } from "./components/sidebar";
import { cn } from "@/lib/utils";
import { RevenueIntelligenceChatPanel } from "./components/revenue-intelligence-chat-panel";
import { RevenueIntelligenceChatProvider } from "@/revenue-intelligence/components/RevenueIntelligenceChatContext";
import { RevenueIntelligenceGlobalChatCatalogue } from "@/revenue-intelligence/components/RevenueIntelligenceGlobalChatCatalogue";
import {
  isRevenueIntelligenceAgentsPath,
  isRevenueIntelligenceHomePath,
  isRevenueIntelligenceProductPath,
} from "@/revenue-intelligence/lib/revenue-intelligence-routes";
import { hasRevenueIntelligenceProductAccess } from "@/revenue-intelligence/lib/revenue-intelligence-product-access";
import { getAuthenticatedDefaultPath } from "@/authentication/auth-redirect";

export const Layout = () => {
  const { data: commissionAccess } = useCommissionAccess();
  const [list, setList] = useState<ListItem[]>([]);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isRevenueIntelligenceChatCollapsed, setIsRevenueIntelligenceChatCollapsed] = useState(true);
  const { currentUser, setDateRange } = useAppStore((state) => state);
  const [location, setLocation] = useLocation();

  useEffect(() => {
    const url = new URL(window.location.href);
    const from = url.searchParams.get("from");
    const to = url.searchParams.get("to");
    if (from || to) {
      setDateRange({
        from: from ? parseISO(from) : undefined,
        to: to ? parseISO(to) : undefined,
      });
    }
  }, [setDateRange]);

  const { data: company, isLoading } = useSWR(
    [
      "companies" as TableType,
      currentUser ? currentUser?.company_id?.toString() : null,
    ],
    ([table, companyId]) => get(table, companyId),
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    }
  );

  // FIXME: Investigate
  useScope({ list });

  const currentUserScope = getScope(list, "user", currentUser?.id);

  const hasRevenueIntelligenceAccess = hasRevenueIntelligenceProductAccess(currentUser);
  const isRevenueIntelligenceRoute = hasRevenueIntelligenceAccess
    && isRevenueIntelligenceProductPath(location);
  const isRevenueIntelligenceHomeRoute = isRevenueIntelligenceHomePath(location);
  const isRevenueIntelligenceAgentsRoute = isRevenueIntelligenceAgentsPath(location);
  const isRevenueIntelligenceFullPageRoute = isRevenueIntelligenceHomeRoute || isRevenueIntelligenceAgentsRoute;


  useEffect(() => {
    if (isLoading) return;
    const list = getList(company, currentUser, commissionAccess);
    setList(list);
    if (!currentUser) return;
    const isSuperAdmin = currentUser.role === "super_admin" || currentUser.is_super_admin;
    const defaultPath = getAuthenticatedDefaultPath({
      commissionsAccess: Boolean(currentUser.company?.product_access_commissions || isSuperAdmin),
      currentPath: window.location.pathname,
      revenueIntelligenceAccess: hasRevenueIntelligenceAccess,
    });
    if (defaultPath) {
      setLocation(
        defaultPath === "/dashboard" && currentUser.role === "employee"
          ? `/dashboard/user/${currentUser.id}`
          : defaultPath,
        { replace: true },
      );
    }
  }, [isLoading, company, commissionAccess, currentUser, hasRevenueIntelligenceAccess, setLocation]);

  if (isLoading) return <Loader />;

  const revenueIntelligenceChatAccessContext = currentUser && hasRevenueIntelligenceAccess
    ? {
        userId: currentUser.id,
        companyId: String(currentUser.company_id),
        role: currentUser.role,
        teamIds: (company?.teams ?? [])
          .filter((team: TODO) => team.users.some((user: TODO) => user.id === currentUser.id))
          .map((team: TODO) => String(team.id)),
      }
    : null;

  return (
    <div className={cn(
      "grid min-h-screen w-full",
      isRevenueIntelligenceRoute
        ? isRevenueIntelligenceFullPageRoute
          ? isSidebarCollapsed
            ? "md:grid-cols-[64px_minmax(0,1fr)] lg:grid-cols-[64px_minmax(0,1fr)]"
            : "md:grid-cols-[190px_minmax(0,1fr)] lg:grid-cols-[190px_minmax(0,1fr)]"
        : isSidebarCollapsed
          ? isRevenueIntelligenceChatCollapsed
            ? "md:grid-cols-[64px_minmax(0,1fr)_64px] lg:grid-cols-[64px_minmax(0,1fr)_64px]"
            : "md:grid-cols-[64px_minmax(0,1fr)_340px] lg:grid-cols-[64px_minmax(0,1fr)_340px]"
          : isRevenueIntelligenceChatCollapsed
            ? "md:grid-cols-[190px_minmax(0,1fr)_64px] lg:grid-cols-[190px_minmax(0,1fr)_64px]"
            : "md:grid-cols-[190px_minmax(0,1fr)_340px] lg:grid-cols-[190px_minmax(0,1fr)_340px]"
        : isSidebarCollapsed
          ? "md:grid-cols-[64px_1fr] lg:grid-cols-[64px_1fr]"
          : "md:grid-cols-[190px_1fr] lg:grid-cols-[190px_1fr]",
      isRevenueIntelligenceRoute && "bg-gray-50"
    )}>

      {/* Sidebar (desktop) */}
      <DesktopSidebarContainer collapsed={isSidebarCollapsed}>
        <AppSidebar
          currentUser={currentUser}
          currentUserScope={currentUserScope}
          collapsed={isSidebarCollapsed}
          onToggleCollapsed={() => setIsSidebarCollapsed(prev => !prev)}
        />
      </DesktopSidebarContainer>

      <RevenueIntelligenceChatProvider accessContext={revenueIntelligenceChatAccessContext}>
        {isRevenueIntelligenceRoute && <RevenueIntelligenceGlobalChatCatalogue />}
        {/* Rest */}
        <div className="flex min-w-0 flex-col">
          {/* Header */}
          <header className="sticky top-0 z-30 flex min-h-14 flex-row items-center justify-center gap-4 border-b border-gray-200/80 bg-white/95 py-2 backdrop-blur supports-[backdrop-filter]:bg-white/85 lg:h-[60px] lg:py-0">
            {/* Main section */}
            <div className="flex min-w-0 flex-1 flex-row flex-wrap items-center justify-between gap-3 px-3 sm:px-6 w-full max-w-[1280px] lg:flex-nowrap">
              {/* Sidebar (mobile) */}
              <Sheet>
                <SheetTrigger asChild>
                  <Button
                    variant="outline"
                    size="icon"
                    className="shrink-0 md:hidden"
                  >
                    <Menu className="h-5 w-5" />
                    <span className="sr-only">Toggle navigation menu</span>
                  </Button>
                </SheetTrigger>
                <SheetContent side="left" className="w-64 flex flex-col px-0">
                  <AppSidebar
                    currentUser={currentUser}
                    currentUserScope={currentUserScope}
                  />
                </SheetContent>
              </Sheet>

              <div className="min-w-0 flex-1">
                <Breadcrumbs />
              </div>
              <div className="flex shrink-0 items-center space-x-4">
                {!isRevenueIntelligenceRoute && (
                  <div>
                    <CalendarDateRangePicker />
                  </div>
                )}
                <UserNav />
              </div>
            </div>
          </header>

          {/* Page content */}
          <div className="flex min-w-0 flex-1 flex-row justify-center">
            <main className="flex min-w-0 w-full max-w-[1280px] flex-1 flex-col gap-4 px-6 pb-5 pt-3">
              <TooltipProvider>
                <Routes />
              </TooltipProvider>
            </main>
          </div>
        </div>

        {isRevenueIntelligenceRoute && (
          <RevenueIntelligenceChatPanel
            collapsed={isRevenueIntelligenceChatCollapsed}
            mode={isRevenueIntelligenceHomeRoute ? "home" : isRevenueIntelligenceAgentsRoute ? "agents" : "panel"}
            onToggleCollapsed={() => setIsRevenueIntelligenceChatCollapsed(prev => !prev)}
          />
        )}
      </RevenueIntelligenceChatProvider>
    </div>
  );
};
